import React, { useEffect, useState } from 'react';
import {
  X,
  Bot,
  MessageSquare,
  FileText,
  Camera,
  BarChart3,
  Code,
  Mail,
  Shield,
  Music,
  Database,
  Zap,
  Brain,
  Globe,
  Cpu,
  Settings,
  Upload,
  Calendar,
  Users,
  Heart,
  Star,
  Bookmark,
  Monitor,
  Smartphone,
  Wifi,
  Cloud,
  Server,
  ExternalLink,
  CheckCircle,
  Info,
  Layers,
  GitBranch
} from 'lucide-react';
import { applicationsMetaService } from '../../../../components/api/applicationsMetaService';
import FilterableMultiSelect from '../../common/FilterableMultiSelect';
import { useTheme } from '../../../../components/common/Theme';

export interface ApplicationFormData {
  name: string;
  description: string;
  category: string;
  icon: string;
  tags: string;
  endpoint: string;
  agents: number;
  color: string;
  frontendEndpoint: string;
  deploymentType: string;
  selectedAgents: string[];
  title: string;
  url: string;
  spacode: string;
  deniedUsers: string[];
  selectedDepartments: string[];
  selectedRoles: string[];
  selectedUsers: string[];
}

interface IconOption {
  name: string;
  icon: any;
  label: string;
}

interface CategoryOption {
  value: string;
  description: string;
}

interface AgentOption {
  id: string;
  name: string;
  type: string;
  status: 'active' | 'inactive';
  description: string;
}

interface ValidationErrors {
  [key: string]: string;
}

interface AddApplicationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (formData: ApplicationFormData) => void;
  editMode?: boolean;
  initialData?: any;
}

const AddApplicationModal: React.FC<AddApplicationModalProps> = ({ 
  isOpen, 
  onClose, 
  onSave,
  editMode = false,
  initialData = null
}) => {
  const [formData, setFormData] = useState<ApplicationFormData>({
    name: '',
    description: '',
    category: '',
    icon: 'Bot',
    tags: '',
    endpoint: '',
    agents: 0,
    color: 'bg-indigo-500',
    frontendEndpoint: '',
    deploymentType: 'spa',
    selectedAgents: [],
    title: '',
    url: '',
    spacode: '',
    deniedUsers: [],
    selectedDepartments: [],
    selectedRoles: [],
    selectedUsers: []
  });

  const [errors, setErrors] = useState<ValidationErrors>({});
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState<'basic' | 'technical' | 'deployment'>('basic');
  const { theme } = useTheme();
  const isLight = theme === 'white';
  const inputCls = isLight
    ? 'bg-white border-neutral-300 text-neutral-900 placeholder-neutral-400'
    : 'bg-neutral-800 border-neutral-700 text-white';
  const labelCls = isLight ? 'text-neutral-700' : 'text-gray-300';
  const borderCls = isLight ? 'border-neutral-200' : 'border-neutral-700';
  const [roleOptions, setRoleOptions] = useState<{ label: string; value: string }[]>([]);
  const [selectedRoles, setSelectedRoles] = useState<string[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [selectedDepartments, setSelectedDepartments] = useState<string[]>([]);
  const [deniedUsers, setDeniedUsers] = useState<string[]>([]);
  const [userOptions, setUserOptions] = useState<{ label: string; value: string }[]>([]);
  const [departmentOptions, setDepartmentOptions] = useState<{ label: string; value: string }[]>([]);

  // Available icon options
  const iconOptions: IconOption[] = [
    { name: 'Bot', icon: Bot, label: 'Bot Assistant' },
    { name: 'MessageSquare', icon: MessageSquare, label: 'Chat & Messaging' },
    { name: 'FileText', icon: FileText, label: 'Document Processing' },
    { name: 'Camera', icon: Camera, label: 'Computer Vision' },
    { name: 'BarChart3', icon: BarChart3, label: 'Analytics & Reporting' },
    { name: 'Code', icon: Code, label: 'Development Tools' },
    { name: 'Mail', icon: Mail, label: 'Email Management' },
    { name: 'Shield', icon: Shield, label: 'Security & Monitoring' },
    { name: 'Music', icon: Music, label: 'Audio Processing' },
    { name: 'Database', icon: Database, label: 'Data Management' },
    { name: 'Zap', icon: Zap, label: 'Automation' },
    { name: 'Brain', icon: Brain, label: 'Artificial Intelligence' },
    { name: 'Globe', icon: Globe, label: 'Web Services' },
    { name: 'Cpu', icon: Cpu, label: 'Processing & Computing' },
    { name: 'Settings', icon: Settings, label: 'Configuration & Settings' },
    { name: 'Upload', icon: Upload, label: 'File Upload & Transfer' },
    { name: 'Calendar', icon: Calendar, label: 'Scheduling & Events' },
    { name: 'Users', icon: Users, label: 'User Management' },
    { name: 'Heart', icon: Heart, label: 'Health & Wellness' },
    { name: 'Star', icon: Star, label: 'Featured & Premium' },
    { name: 'Bookmark', icon: Bookmark, label: 'Bookmarks & Saved Items' },
    { name: 'Monitor', icon: Monitor, label: 'Monitoring & Dashboards' },
    { name: 'Smartphone', icon: Smartphone, label: 'Mobile Applications' },
    { name: 'Wifi', icon: Wifi, label: 'Network & Connectivity' },
    { name: 'Cloud', icon: Cloud, label: 'Cloud Services' }
  ];

  // Categories with descriptions
  const categoryOptions: CategoryOption[] = [
    { value: 'Communication', description: 'Chat, messaging, and communication tools' },
    { value: 'Document Processing', description: 'Text analysis, OCR, and document management' },
    { value: 'Computer Vision', description: 'Image and video processing applications' },
    { value: 'Analytics', description: 'Data analysis and business intelligence' },
    { value: 'Development', description: 'Code analysis and development tools' },
    { value: 'Productivity', description: 'Workflow automation and productivity enhancement' },
    { value: 'Security', description: 'Security monitoring and threat detection' },
    { value: 'Audio Processing', description: 'Speech, music, and audio analysis' },
    { value: 'Finance', description: 'Financial analysis and trading applications' },
    { value: 'Healthcare', description: 'Medical diagnosis and healthcare tools' },
    { value: 'Education', description: 'Learning and educational applications' },
    { value: 'IoT', description: 'Internet of Things and device management' },
    { value: 'E-commerce', description: 'Online retail and marketplace solutions' },
    { value: 'Media', description: 'Content creation and media processing' }
  ];

  // Mock available agents
  const availableAgents: AgentOption[] = [
    { id: '1', name: 'Text Summarizer', type: 'NLP', status: 'active', description: 'Advanced text summarization agent' },
    { id: '2', name: 'Sentiment Analyzer', type: 'NLP', status: 'active', description: 'Real-time sentiment analysis' },
    { id: '3', name: 'Image Classifier', type: 'Vision', status: 'active', description: 'Multi-class image classification' },
    { id: '4', name: 'Object Detector', type: 'Vision', status: 'active', description: 'Real-time object detection' },
    { id: '5', name: 'Data Transformer', type: 'Analytics', status: 'active', description: 'Data transformation and ETL' },
    { id: '6', name: 'Fraud Detector', type: 'Security', status: 'active', description: 'Advanced fraud detection' },
    { id: '7', name: 'Speech Recognizer', type: 'Audio', status: 'active', description: 'Speech-to-text conversion' },
    { id: '8', name: 'Code Reviewer', type: 'Development', status: 'inactive', description: 'Automated code review' },
    { id: '9', name: 'Price Optimizer', type: 'Analytics', status: 'active', description: 'Dynamic pricing optimization' },
    { id: '10', name: 'Email Parser', type: 'NLP', status: 'active', description: 'Email content extraction' }
  ];

  // Load initial data when in edit mode
  useEffect(() => {
    if (editMode && initialData) {
      console.log(initialData);
      setFormData({
        name: initialData.name || '',
        title: initialData.name || '',
        description: initialData.description || '',
        category: initialData.category || '',
        icon: (typeof initialData.icon === 'string' ? initialData.icon : 'Bot'),
        tags: Array.isArray(initialData.tags) ? initialData.tags.join(', ') : '',
        endpoint: initialData.endpoint || '',
        agents: initialData.agents || 0,
        color: initialData.color || 'bg-indigo-500',
        frontendEndpoint: initialData.frontendUrl || '',
        url: initialData.frontendUrl || '',
        deploymentType: 'spa',
        selectedAgents: [],
        spacode: initialData.spacode || '',
        deniedUsers: [],
        selectedDepartments: [],
        selectedRoles: [],
        selectedUsers: []
      });
    }
  }, [editMode, initialData]);

  useEffect(() => {
    // Mock data - replace with actual API calls
    setRoleOptions([
      { label: 'Admin', value: 'admin' },
      { label: 'Developer', value: 'developer' },
      { label: 'Analyst', value: 'analyst' },
      { label: 'Viewer', value: 'viewer' }
    ]);
    setDepartmentOptions([
      { label: 'HR', value: 'HR' },
      { label: 'Admin', value: 'Admin' },
      { label: 'Finance', value: 'Finance' },
      { label: 'IT', value: 'IT' }
    ]);

    setUserOptions([
      { label: 'John Doe', value: 'user-1' },
      { label: 'Jane Smith', value: 'user-2' },
      { label: 'Bob Johnson', value: 'user-3' },
      { label: 'Alice Williams', value: 'user-4' }
    ]);
  }, []);

  // Validation function
  const validateForm = (): boolean => {
    const newErrors: ValidationErrors = {};

    if (!formData.title.trim()) {
      newErrors.title = 'Application name is required';
    } else if (formData.title.length < 3) {
      newErrors.title = 'Name must be at least 3 characters long';
    }

    if (!formData.description.trim()) {
      newErrors.description = 'Description is required';
    } else if (formData.description.length < 10) {
      newErrors.description = 'Description must be at least 10 characters long';
    }

    if (!formData.category) {
      newErrors.category = 'Category is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const syncedFormData = {
        ...formData,
        name: formData.title,
        frontendEndpoint: formData.url,
      };

      if (editMode && initialData) {
        // UPDATE mode
        const updatePayload = {
          id: initialData.id,
          title: syncedFormData.title,
          description: syncedFormData.description,
          tags: syncedFormData.tags,
          status: 0,
          icon: syncedFormData.icon,
          url: syncedFormData.url,
          spacode: syncedFormData.spacode,
          version: initialData.version || "1.0.0",
          createdBy: initialData.createdBy || "current-user",
          tenantId: localStorage.getItem('tenant_id'),
          mappedAgentsCount: syncedFormData.selectedAgents.length.toString(),
          category: syncedFormData.category
        };

        await applicationsMetaService.update(updatePayload);
      } else {
        // CREATE mode
        const applicationPayload = {
          id: "00000000-0000-0000-0000-000000000000",
          title: syncedFormData.title,
          description: syncedFormData.description,
          tags: syncedFormData.tags,
          status: 0,
          icon: syncedFormData.icon,
          url: syncedFormData.url,
          spacode: syncedFormData.spacode,
          version: "1.0.0",
          createdBy: "current-user",
          tenantId: localStorage.getItem('tenant_id'),
          mappedAgentsCount: syncedFormData.selectedAgents.length.toString(),
          category: syncedFormData.category
        };

        await applicationsMetaService.create(applicationPayload);
      }

      const completeFormData = {
        ...syncedFormData,
        agents: syncedFormData.selectedAgents.length,
        endpoint: '/api/v1/' + syncedFormData.title.toLowerCase().replace(/\s+/g, '-')
      };

      onSave(completeFormData);

      // Reset form and close modal
      setFormData({
        name: '',
        description: '',
        category: '',
        icon: 'Bot',
        tags: '',
        endpoint: '',
        agents: 0,
        color: 'bg-indigo-500',
        frontendEndpoint: '',
        deploymentType: 'spa',
        selectedAgents: [],
        title: '',
        url: '',
        spacode: '',
        deniedUsers: [],
        selectedDepartments: [],
        selectedRoles: [],
        selectedUsers: []
      });
      setErrors({});
      setActiveTab('basic');
      onClose();

    } catch (error) {
      console.error('Error saving application:', error);
      setErrors({ submit: editMode ? 'Failed to update application. Please try again.' : 'Failed to create application. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (field: keyof ApplicationFormData, value: string | number | string[]): void => {
    const updates: Partial<ApplicationFormData> = { [field]: value };

    if (field === 'title') {
      updates.name = value as string;
    } else if (field === 'name') {
      updates.title = value as string;
    }

    if (field === 'url') {
      updates.frontendEndpoint = value as string;
    } else if (field === 'frontendEndpoint') {
      updates.url = value as string;
    }

    setFormData({ ...formData, ...updates });

    if (errors[field]) {
      setErrors({ ...errors, [field]: '' });
    }
  };

  const handleAgentSelection = (agentId: string): void => {
    const currentSelected = formData.selectedAgents;
    const newSelected = currentSelected.includes(agentId)
      ? currentSelected.filter(id => id !== agentId)
      : [...currentSelected, agentId];

    handleInputChange('selectedAgents', newSelected);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
      <div className={`rounded-lg w-full max-w-4xl max-h-[90vh] overflow-hidden border flex flex-col ${isLight ? 'bg-white border-neutral-200 shadow-xl' : 'bg-neutral-900 border-neutral-700'}`}>
        {/* Header */}
        <div className={`flex items-center justify-between p-5 border-b ${borderCls}`}>
          <div>
            <h2 className={`text-lg font-semibold ${isLight ? 'text-neutral-900' : 'text-white'}`}>
              {editMode ? 'Edit Application' : 'Create New Application'}
            </h2>
            <p className={`text-xs mt-1 ${isLight ? 'text-neutral-500' : 'text-gray-400'}`}>
              {editMode ? 'Update application configuration and settings' : 'Configure and deploy a new AI-powered application'}
            </p>
          </div>
          <button
            onClick={onClose}
            className={`p-1 rounded-md transition-colors ${isLight ? 'text-neutral-400 hover:text-neutral-900 hover:bg-neutral-100' : 'text-gray-400 hover:text-white hover:bg-neutral-800'}`}
            type="button"
          >
            <X size={18} />
          </button>
        </div>

        {/* Tab Navigation */}
        <div className={`flex border-b ${borderCls}`}>
          {[
            { id: 'basic', label: 'Basic Info', icon: Info },
            { id: 'technical', label: 'Technical', icon: Server },
            { id: 'deployment', label: 'Deployment', icon: ExternalLink }
          ].map((tab) => {
            const TabIcon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={`flex items-center px-4 py-3 text-xs font-medium transition-colors ${activeTab === tab.id
                  ? (isLight ? 'text-indigo-600 border-b-2 border-indigo-600 bg-indigo-50/50' : 'text-indigo-400 border-b-2 border-indigo-400 bg-neutral-800/50')
                  : (isLight ? 'text-neutral-500 hover:text-neutral-900 hover:bg-neutral-50' : 'text-gray-400 hover:text-white hover:bg-neutral-800/30')
                  }`}
              >
                <TabIcon size={14} className="mr-2" />
                {tab.label}
              </button>
            );
          })}
        </div>

        <div className="flex flex-col flex-1 min-h-0">
          <div className="flex-1 overflow-y-auto p-5">
            {/* Basic Information Tab */}
            {activeTab === 'basic' && (
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Application Name */}
                  <div>
                    <label className={`block text-xs font-medium mb-1 ${labelCls}`}>
                      Application Name *
                    </label>
                    <input
                      type="text"
                      value={formData.title}
                      onChange={(e) => handleInputChange('title', e.target.value)}
                      className={`w-full border px-3 py-2 rounded-md text-xs focus:outline-none focus:ring-2 focus:ring-indigo-500 ${inputCls} ${errors.title ? 'border-red-500' : borderCls
                        }`}
                      placeholder="Enter application name"
                      maxLength={100}
                    />
                    {errors.title && <p className="text-red-400 text-xs mt-1">{errors.title}</p>}
                  </div>

                  {/* Category */}
                  <div>
                    <label className={`block text-xs font-medium mb-1 ${labelCls}`}>
                      Category *
                    </label>
                    <select
                      value={formData.category}
                      onChange={(e) => handleInputChange('category', e.target.value)}
                      className={`w-full border px-3 py-2 rounded-md text-xs focus:outline-none focus:ring-2 focus:ring-indigo-500 ${inputCls} ${errors.category ? 'border-red-500' : borderCls
                        }`}
                    >
                      <option value="">Select category</option>
                      {categoryOptions.map(category => (
                        <option key={category.value} value={category.value}>
                          {category.value}
                        </option>
                      ))}
                    </select>
                    {errors.category && <p className="text-red-400 text-xs mt-1">{errors.category}</p>}
                  </div>
                </div>

                {/* Description */}
                <div>
                  <label className={`block text-xs font-medium mb-1 ${labelCls}`}>
                    Description *
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => handleInputChange('description', e.target.value)}
                    className={`w-full border px-3 py-2 rounded-md text-xs focus:outline-none focus:ring-2 focus:ring-indigo-500 ${inputCls} ${errors.description ? 'border-red-500' : borderCls
                      }`}
                    rows={3}
                    placeholder="Describe the application's purpose, features, and benefits..."
                    maxLength={500}
                  />
                  {errors.description && <p className="text-red-400 text-xs mt-1">{errors.description}</p>}
                  <p className="text-xs text-gray-500 mt-1">{formData.description.length}/500 characters</p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <FilterableMultiSelect
                      label="Select Roles"
                      options={roleOptions}
                      selected={selectedRoles}
                      setSelected={setSelectedRoles}
                    />
                  </div>

                  <div>
                    <FilterableMultiSelect
                      label="Select Users"
                      options={userOptions}
                      selected={selectedUsers}
                      setSelected={setSelectedUsers}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <FilterableMultiSelect
                      label="Denied Users"
                      options={userOptions}
                      selected={deniedUsers}
                      setSelected={setDeniedUsers}
                    />
                  </div>

                  <div>
                    <FilterableMultiSelect
                      label="Allowed Departments"
                      options={departmentOptions}
                      selected={selectedDepartments}
                      setSelected={setSelectedDepartments}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Icon Selection */}
                  <div>
                    <label className={`block text-xs font-medium mb-1 ${labelCls}`}>
                      Application Icon
                    </label>
                    <select
                      value={formData.icon}
                      onChange={(e) => handleInputChange('icon', e.target.value)}
                      className={`w-full border px-3 py-2 rounded-md text-xs focus:outline-none focus:ring-2 focus:ring-indigo-500 ${inputCls} ${borderCls}`}
                    >
                      {iconOptions.map((option) => (
                        <option key={option.name} value={option.name}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Tags */}
                  <div>
                    <label className={`block text-xs font-medium mb-1 ${labelCls}`}>
                      Tags (comma-separated)
                    </label>
                    <input
                      type="text"
                      value={formData.tags}
                      onChange={(e) => handleInputChange('tags', e.target.value)}
                      className={`w-full border px-3 py-2 rounded-md text-xs focus:outline-none focus:ring-2 focus:ring-indigo-500 ${inputCls} ${borderCls}`}
                      placeholder="AI, Analytics, Enterprise, Automation"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Technical Configuration Tab */}
            {activeTab === 'technical' && (
              <div className="space-y-4">
                {/* Agent Selection */}
                <div>
                  <label className={`block text-xs font-medium mb-2 ${labelCls}`}>
                    Select Agents <span className={isLight ? 'text-neutral-400' : 'text-gray-500'}>({formData.selectedAgents.length} selected)</span>
                  </label>
                  <div className={`border rounded-md max-h-48 overflow-y-auto ${isLight ? 'bg-white border-neutral-200' : 'bg-neutral-800 border-neutral-700'}`}>
                    {availableAgents.map((agent) => (
                      <div key={agent.id} className={`flex items-center p-3 border-b last:border-b-0 ${borderCls}`}>
                        <input
                          type="checkbox"
                          id={`agent-${agent.id}`}
                          checked={formData.selectedAgents.includes(agent.id)}
                          onChange={() => handleAgentSelection(agent.id)}
                          className="mr-3 rounded border-neutral-600 text-indigo-600 focus:ring-indigo-500"
                          disabled={agent.status === 'inactive'}
                        />
                        <div className="flex-1">
                          <label htmlFor={`agent-${agent.id}`} className="flex items-center justify-between cursor-pointer">
                            <div>
                              <p className={`text-xs font-medium ${agent.status === 'active' ? (isLight ? 'text-neutral-900' : 'text-white') : 'text-gray-500'}`}>
                                {agent.name}
                              </p>
                              <p className={`text-xs ${isLight ? 'text-neutral-500' : 'text-gray-400'}`}>{agent.description}</p>
                            </div>
                            <div className="flex items-center space-x-2">
                              <span className={`px-2 py-0.5 rounded-full text-xs ${agent.status === 'active'
                                ? 'bg-green-400/10 text-green-400'
                                : 'bg-gray-400/10 text-gray-400'
                                }`}>
                                {agent.type}
                              </span>
                              <span className={`w-2 h-2 rounded-full ${agent.status === 'active' ? 'bg-green-400' : 'bg-gray-400'
                                }`} />
                            </div>
                          </label>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Deployment Configuration Tab */}
            {activeTab === 'deployment' && (
              <div className="space-y-4">
                {/* Frontend Configuration */}
                <div className={`space-y-4 p-4 rounded-lg border ${isLight ? 'bg-neutral-50 border-neutral-200' : 'bg-neutral-800/50 border-neutral-700'}`}>
                  <div className="flex items-center space-x-2">
                    <Globe size={16} className="text-indigo-400" />
                    <h3 className={`text-sm font-medium ${isLight ? 'text-neutral-900' : 'text-white'}`}>Frontend Configuration</h3>
                  </div>

                  <div>
                    <label className={`block text-xs font-medium mb-1 ${labelCls}`}>
                      Frontend URL Route
                    </label>
                    <input
                      type="text"
                      value={formData.url}
                      onChange={(e) => handleInputChange('url', e.target.value)}
                      className={`w-full border px-3 py-2 rounded-md text-xs focus:outline-none focus:ring-2 focus:ring-indigo-500 font-mono ${inputCls} ${errors.url ? 'border-red-500' : borderCls
                        }`}
                      placeholder="https://your-app.yourdomain.com"
                    />
                    {errors.url && <p className="text-red-400 text-xs mt-1">{errors.url}</p>}
                    <p className="text-xs text-gray-500 mt-1">The public URL where your frontend will be accessible</p>
                  </div>

                  {/* SPA Code */}
                  <div>
                    <label className={`block text-xs font-medium mb-1 ${labelCls}`}>
                      SPA Code
                    </label>
                    <textarea
                      value={formData.spacode}
                      onChange={(e) => handleInputChange('spacode', e.target.value)}
                      className={`w-full border px-3 py-2 rounded-md text-xs focus:outline-none focus:ring-2 focus:ring-indigo-500 font-mono ${inputCls} ${borderCls}`}
                      rows={8}
                      placeholder="Enter your SPA code here..."
                    />
                    <p className="text-xs text-gray-500 mt-1">Single Page Application code for your frontend</p>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className={`block text-xs font-medium mb-1 ${labelCls}`}>
                        Deploy
                      </label>
                      <input
                        type="file"
                        className={`w-full border px-3 py-2 rounded-md text-xs focus:outline-none focus:ring-2 focus:ring-indigo-500 font-mono ${inputCls} ${borderCls}`}
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Error Display */}
            {errors.submit && (
              <div className="mt-4 p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
                <p className="text-red-400 text-xs">{errors.submit}</p>
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className={`flex items-center justify-between p-5 border-t ${borderCls}`}>
            <div className="flex items-center space-x-2">
              <GitBranch size={14} className={isLight ? 'text-neutral-400' : 'text-gray-400'} />
              <span className={`text-xs ${isLight ? 'text-neutral-500' : 'text-gray-400'}`}>
                {activeTab === 'basic' && 'Step 1 of 3: Basic Information'}
                {activeTab === 'technical' && 'Step 2 of 3: Technical Configuration'}
                {activeTab === 'deployment' && 'Step 3 of 3: Deployment Setup'}
              </span>
            </div>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={onClose}
                className={`px-4 py-2 rounded-md text-xs transition-colors ${isLight ? 'bg-neutral-100 hover:bg-neutral-200 text-neutral-700' : 'bg-neutral-700 hover:bg-neutral-600 text-white'}`}
                disabled={isSubmitting}
              >
                Cancel
              </button>
              {activeTab !== 'deployment' ? (
                <button
                  type="button"
                  onClick={() => {
                    if (activeTab === 'basic') setActiveTab('technical');
                    else if (activeTab === 'technical') setActiveTab('deployment');
                  }}
                  className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-md text-xs transition-colors"
                >
                  Next Step
                </button>
              ) : (
                <button
                  type="submit"
                  onClick={handleSubmit}
                  className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-md text-xs transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={isSubmitting}
                >
                  {isSubmitting 
                    ? (editMode ? 'Updating Application...' : 'Creating Application...') 
                    : (editMode ? 'Update Application' : 'Create Application')}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddApplicationModal;


// import React, { useEffect, useState } from 'react';
// import {
//   X,
//   Bot,
//   MessageSquare,
//   FileText,
//   Camera,
//   BarChart3,
//   Code,
//   Mail,
//   Shield,
//   Music,
//   Database,
//   Zap,
//   Brain,
//   Globe,
//   Cpu,
//   Settings,
//   Upload,
//   Calendar,
//   Users,
//   Heart,
//   Star,
//   Bookmark,
//   Monitor,
//   Smartphone,
//   Wifi,
//   Cloud,
//   Server,
//   ExternalLink,
//   CheckCircle,
//   Info,
//   Layers,
//   GitBranch
// } from 'lucide-react';
// import { applicationsMetaService } from '../../../../components/api/applicationsMetaService';
// import FilterableMultiSelect from '../../common/FilterableMultiSelect';

// // Mock API service - replace with your actual import 
// export interface ApplicationFormData {
//   name: string;           // Changed from 'title'
//   description: string;
//   category: string;
//   icon: string;
//   tags: string;
//   endpoint: string;       // Added (was missing)
//   agents: number;         // Added (was missing)
//   color: string;          // Added (was missing)
//   frontendEndpoint: string; // Changed from 'url'
//   deploymentType: string; // Added (was missing)
//   selectedAgents: string[];
//   // New API fields (optional for backward compatibility)
//   title: string;
//   url: string;
//   spacode: string;
//   selectedDepartments: string[];
//   selectedRoles: string[];      // ADD
//   selectedUsers: string[];      // ADD
//   deniedUsers: string[];
// }

// interface IconOption {
//   name: string;
//   icon: any;
//   label: string;
// }

// interface CategoryOption {
//   value: string;
//   description: string;
// }

// interface AgentOption {
//   id: string;
//   name: string;
//   type: string;
//   status: 'active' | 'inactive';
//   description: string;
// }

// interface ValidationErrors {
//   [key: string]: string;
// }

// interface AddApplicationModalProps {
//   isOpen: boolean;
//   onClose: () => void;
//   onSave: (formData: ApplicationFormData) => void;
// }

// const AddApplicationModal: React.FC<AddApplicationModalProps> = ({ isOpen, onClose, onSave }) => {
//   const [formData, setFormData] = useState<ApplicationFormData>({
//     name: '',
//     description: '',
//     category: '',
//     icon: 'Bot',
//     tags: '',
//     endpoint: '',
//     agents: 0,
//     color: 'bg-indigo-500',
//     frontendEndpoint: '',
//     deploymentType: 'spa',
//     selectedAgents: [],
//     // New API fields
//     title: '',
//     url: '',
//     spacode: '',
//     deniedUsers: [],
//     selectedDepartments: [],
//     selectedRoles: [],
//     selectedUsers: []
//   });

//   const [errors, setErrors] = useState<ValidationErrors>({});
//   const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
//   const [activeTab, setActiveTab] = useState<'basic' | 'technical' | 'deployment'>('basic');
//   const [roleOptions, setRoleOptions] = useState<{ label: string; value: string }[]>([]);
//   const [selectedRoles, setSelectedRoles] = useState<string[]>([]);
//   const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
//   const [selectedDepartments, setSelectedDepartments] = useState<string[]>([]);
//   const [deniedUsers, setDeniedUsers] = useState<string[]>([]);

//   // Available icon options
//   const iconOptions: IconOption[] = [
//     { name: 'Bot', icon: Bot, label: 'Bot Assistant' },
//     { name: 'MessageSquare', icon: MessageSquare, label: 'Chat & Messaging' },
//     { name: 'FileText', icon: FileText, label: 'Document Processing' },
//     { name: 'Camera', icon: Camera, label: 'Computer Vision' },
//     { name: 'BarChart3', icon: BarChart3, label: 'Analytics & Reporting' },
//     { name: 'Code', icon: Code, label: 'Development Tools' },
//     { name: 'Mail', icon: Mail, label: 'Email Management' },
//     { name: 'Shield', icon: Shield, label: 'Security & Monitoring' },
//     { name: 'Music', icon: Music, label: 'Audio Processing' },
//     { name: 'Database', icon: Database, label: 'Data Management' },
//     { name: 'Zap', icon: Zap, label: 'Automation' },
//     { name: 'Brain', icon: Brain, label: 'Artificial Intelligence' },
//     { name: 'Globe', icon: Globe, label: 'Web Services' },
//     { name: 'Cpu', icon: Cpu, label: 'Processing & Computing' },
//     { name: 'Settings', icon: Settings, label: 'Configuration & Settings' },
//     { name: 'Upload', icon: Upload, label: 'File Upload & Transfer' },
//     { name: 'Calendar', icon: Calendar, label: 'Scheduling & Events' },
//     { name: 'Users', icon: Users, label: 'User Management' },
//     { name: 'Heart', icon: Heart, label: 'Health & Wellness' },
//     { name: 'Star', icon: Star, label: 'Featured & Premium' },
//     { name: 'Bookmark', icon: Bookmark, label: 'Bookmarks & Saved Items' },
//     { name: 'Monitor', icon: Monitor, label: 'Monitoring & Dashboards' },
//     { name: 'Smartphone', icon: Smartphone, label: 'Mobile Applications' },
//     { name: 'Wifi', icon: Wifi, label: 'Network & Connectivity' },
//     { name: 'Cloud', icon: Cloud, label: 'Cloud Services' }
//   ];

//   // Categories with descriptions
//   const categoryOptions: CategoryOption[] = [
//     { value: 'Communication', description: 'Chat, messaging, and communication tools' },
//     { value: 'Document Processing', description: 'Text analysis, OCR, and document management' },
//     { value: 'Computer Vision', description: 'Image and video processing applications' },
//     { value: 'Analytics', description: 'Data analysis and business intelligence' },
//     { value: 'Development', description: 'Code analysis and development tools' },
//     { value: 'Productivity', description: 'Workflow automation and productivity enhancement' },
//     { value: 'Security', description: 'Security monitoring and threat detection' },
//     { value: 'Audio Processing', description: 'Speech, music, and audio analysis' },
//     { value: 'Finance', description: 'Financial analysis and trading applications' },
//     { value: 'Healthcare', description: 'Medical diagnosis and healthcare tools' },
//     { value: 'Education', description: 'Learning and educational applications' },
//     { value: 'IoT', description: 'Internet of Things and device management' },
//     { value: 'E-commerce', description: 'Online retail and marketplace solutions' },
//     { value: 'Media', description: 'Content creation and media processing' }
//   ];

//   // Mock available agents
//   const availableAgents: AgentOption[] = [
//     { id: '1', name: 'Text Summarizer', type: 'NLP', status: 'active', description: 'Advanced text summarization agent' },
//     { id: '2', name: 'Sentiment Analyzer', type: 'NLP', status: 'active', description: 'Real-time sentiment analysis' },
//     { id: '3', name: 'Image Classifier', type: 'Vision', status: 'active', description: 'Multi-class image classification' },
//     { id: '4', name: 'Object Detector', type: 'Vision', status: 'active', description: 'Real-time object detection' },
//     { id: '5', name: 'Data Transformer', type: 'Analytics', status: 'active', description: 'Data transformation and ETL' },
//     { id: '6', name: 'Fraud Detector', type: 'Security', status: 'active', description: 'Advanced fraud detection' },
//     { id: '7', name: 'Speech Recognizer', type: 'Audio', status: 'active', description: 'Speech-to-text conversion' },
//     { id: '8', name: 'Code Reviewer', type: 'Development', status: 'inactive', description: 'Automated code review' },
//     { id: '9', name: 'Price Optimizer', type: 'Analytics', status: 'active', description: 'Dynamic pricing optimization' },
//     { id: '10', name: 'Email Parser', type: 'NLP', status: 'active', description: 'Email content extraction' }
//   ];

//   // Validation function
//   const validateForm = (): boolean => {
//     const newErrors: ValidationErrors = {};

//     if (!formData.name.trim()) {                    // Changed from 'title'
//       newErrors.name = 'Application name is required';
//     } else if (formData.name.length < 3) {
//       newErrors.name = 'Name must be at least 3 characters long';
//     }

//     if (!formData.description.trim()) {
//       newErrors.description = 'Description is required';
//     } else if (formData.description.length < 10) {
//       newErrors.description = 'Description must be at least 10 characters long';
//     }

//     if (!formData.category) {
//       newErrors.category = 'Category is required';
//     }

//     setErrors(newErrors);
//     return Object.keys(newErrors).length === 0;
//   };
//   const [userOptions, setUserOptions] = useState<{ label: string; value: string }[]>([]);
//   const [departmentOptions, setDepartmentOptions] = useState<{ label: string; value: string }[]>([]);

//   useEffect(() => {
//     // Mock data - replace with actual API calls
//     setRoleOptions([
//       { label: 'Admin', value: 'admin' },
//       { label: 'Developer', value: 'developer' },
//       { label: 'Analyst', value: 'analyst' },
//       { label: 'Viewer', value: 'viewer' }
//     ]);
//     setDepartmentOptions([
//       { label: 'HR', value: 'HR' },
//       { label: 'Admin', value: 'Admin' },
//       { label: 'Finance', value: 'Finance' },
//       { label: 'IT', value: 'IT' }
//     ]);

//     setUserOptions([
//       { label: 'John Doe', value: 'user-1' },
//       { label: 'Jane Smith', value: 'user-2' },
//       { label: 'Bob Johnson', value: 'user-3' },
//       { label: 'Alice Williams', value: 'user-4' }
//     ]);
//   }, []);

//   const handleSubmit = async (e: React.FormEvent): Promise<void> => {
//     e.preventDefault();

//     if (!validateForm()) {
//       return;
//     }

//     setIsSubmitting(true);

//     try {
//       // Ensure fields are synced before submission
//       const syncedFormData = {
//         ...formData,
//         name: formData.title, // Ensure name matches title
//         frontendEndpoint: formData.url, // Ensure frontendEndpoint matches url
//       };

//       const applicationPayload = {
//         id: "00000000-0000-0000-0000-000000000000",
//         title: syncedFormData.title,
//         description: syncedFormData.description,
//         tags: syncedFormData.tags,
//         status: 0,
//         icon: syncedFormData.icon,
//         url: syncedFormData.url,
//         spacode: syncedFormData.spacode,
//         version: "1.0.0",
//         createdBy: "current-user",
//         tenantId: localStorage.getItem('tenant_id'),
//         mappedAgentsCount: syncedFormData.selectedAgents.length.toString(),
//         category: syncedFormData.category
//       };

//       const response = await applicationsMetaService.create(applicationPayload);
//       console.log('Application created successfully:', response);

//       // Complete form data for onSave callback
//       const completeFormData = {
//         ...syncedFormData,
//         agents: syncedFormData.selectedAgents.length,
//         endpoint: '/api/v1/' + syncedFormData.title.toLowerCase().replace(/\s+/g, '-')
//       };

//       onSave(completeFormData);

//       // Reset form and close modal
//       setFormData({
//         name: '',
//         description: '',
//         category: '',
//         icon: 'Bot',
//         tags: '',
//         endpoint: '',
//         agents: 0,
//         color: 'bg-indigo-500',
//         frontendEndpoint: '',
//         deploymentType: 'spa',
//         selectedAgents: [],
//         title: '',
//         url: '',
//         spacode: '',
//         deniedUsers: [],
//         selectedDepartments: [],
//         selectedRoles: [],
//         selectedUsers: []
//       });
//       setErrors({});
//       setActiveTab('basic');
//       onClose();

//     } catch (error) {
//       console.error('Error creating application:', error);
//       setErrors({ submit: 'Failed to create application. Please try again.' });
//     } finally {
//       setIsSubmitting(false);
//     }
//   };

//   const handleInputChange = (field: keyof ApplicationFormData, value: string | number | string[]): void => {
//     const updates: Partial<ApplicationFormData> = { [field]: value };

//     // Sync name and title fields
//     if (field === 'title') {
//       updates.name = value as string;
//     } else if (field === 'name') {
//       updates.title = value as string;
//     }

//     // Sync url and frontendEndpoint
//     if (field === 'url') {
//       updates.frontendEndpoint = value as string;
//     } else if (field === 'frontendEndpoint') {
//       updates.url = value as string;
//     }

//     setFormData({ ...formData, ...updates });

//     if (errors[field]) {
//       setErrors({ ...errors, [field]: '' });
//     }
//   };

//   const handleAgentSelection = (agentId: string): void => {
//     const currentSelected = formData.selectedAgents;
//     const newSelected = currentSelected.includes(agentId)
//       ? currentSelected.filter(id => id !== agentId)
//       : [...currentSelected, agentId];

//     handleInputChange('selectedAgents', newSelected);
//   };

//   const getSelectedIconComponent = (): any => {
//     const selectedIcon = iconOptions.find(option => option.name === formData.icon);
//     return selectedIcon ? selectedIcon.icon : Bot;
//   };

//   if (!isOpen) return null;

//   return (
//     <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
//       <div className="bg-neutral-900 border border-neutral-700 rounded-lg w-full max-w-4xl max-h-[90vh] overflow-hidden">
//         {/* Header */}
//         <div className="flex items-center justify-between p-5 border-b border-neutral-700">
//           <div>
//             <h2 className="text-lg font-semibold text-white">Create New Application</h2>
//             <p className="text-xs text-gray-400 mt-1">Configure and deploy a new AI-powered application</p>
//           </div>
//           <button
//             onClick={onClose}
//             className="text-gray-400 hover:text-white p-1 rounded-md hover:bg-neutral-800 transition-colors"
//             type="button"
//           >
//             <X size={18} />
//           </button>
//         </div>

//         {/* Tab Navigation */}
//         <div className="flex border-b border-neutral-700">
//           {[
//             { id: 'basic', label: 'Basic Info', icon: Info },
//             { id: 'technical', label: 'Technical', icon: Server },
//             { id: 'deployment', label: 'Deployment', icon: ExternalLink }
//           ].map((tab) => {
//             const TabIcon = tab.icon;
//             return (
//               <button
//                 key={tab.id}
//                 onClick={() => setActiveTab(tab.id as any)}
//                 className={`flex items-center px-4 py-3 text-xs font-medium transition-colors ${activeTab === tab.id
//                   ? 'text-indigo-400 border-b-2 border-indigo-400 bg-neutral-800/50'
//                   : 'text-gray-400 hover:text-white hover:bg-neutral-800/30'
//                   }`}
//               >
//                 <TabIcon size={14} className="mr-2" />
//                 {tab.label}
//               </button>
//             );
//           })}
//         </div>

//         <div className="flex flex-col h-full">
//           <div className="flex-1 overflow-y-auto p-5">
//             {/* Basic Information Tab */}
//             {activeTab === 'basic' && (
//               <div className="space-y-4">
//                 <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
//                   {/* Application Name */}
//                   <div>
//                     <label className={`block text-xs font-medium mb-1 ${labelCls}`}>
//                       Application Name *
//                     </label>
//                     <input
//                       type="text"
//                       value={formData.title}
//                       onChange={(e) => handleInputChange('title', e.target.value)}
//                       className={`w-full bg-neutral-800 border text-white px-3 py-2 rounded-md text-xs focus:outline-none focus:ring-2 focus:ring-indigo-500 ${errors.title ? 'border-red-500' : 'border-neutral-700'
//                         }`}
//                       placeholder="Enter application name"
//                       maxLength={100}
//                     />
//                     {errors.title && <p className="text-red-400 text-xs mt-1">{errors.title}</p>}
//                   </div>

//                   {/* Category */}
//                   <div>
//                     <label className={`block text-xs font-medium mb-1 ${labelCls}`}>
//                       Category *
//                     </label>
//                     <select
//                       value={formData.category}
//                       onChange={(e) => handleInputChange('category', e.target.value)}
//                       className={`w-full bg-neutral-800 border text-white px-3 py-2 rounded-md text-xs focus:outline-none focus:ring-2 focus:ring-indigo-500 ${errors.category ? 'border-red-500' : 'border-neutral-700'
//                         }`}
//                     >
//                       <option value="">Select category</option>
//                       {categoryOptions.map(category => (
//                         <option key={category.value} value={category.value}>
//                           {category.value}
//                         </option>
//                       ))}
//                     </select>
//                     {errors.category && <p className="text-red-400 text-xs mt-1">{errors.category}</p>}
//                   </div>
//                 </div>

//                 {/* Description */}
//                 <div>
//                   <label className={`block text-xs font-medium mb-1 ${labelCls}`}>
//                     Description *
//                   </label>
//                   <textarea
//                     value={formData.description}
//                     onChange={(e) => handleInputChange('description', e.target.value)}
//                     className={`w-full bg-neutral-800 border text-white px-3 py-2 rounded-md text-xs focus:outline-none focus:ring-2 focus:ring-indigo-500 ${errors.description ? 'border-red-500' : 'border-neutral-700'
//                       }`}
//                     rows={3}
//                     placeholder="Describe the application's purpose, features, and benefits..."
//                     maxLength={500}
//                   />
//                   {errors.description && <p className="text-red-400 text-xs mt-1">{errors.description}</p>}
//                   <p className="text-xs text-gray-500 mt-1">{formData.description.length}/500 characters</p>
//                 </div>

//                 <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
//                   <div> 
//                     <FilterableMultiSelect
//                       label="Select Roles"
//                       options={roleOptions}
//                       selected={selectedRoles}
//                       setSelected={setSelectedRoles}
//                     />
//                   </div>

//                   <div> 
//                     <FilterableMultiSelect
//                       label="Select Users"
//                       options={userOptions}
//                       selected={selectedUsers}
//                       setSelected={setSelectedUsers}
//                     />
//                   </div>
//                 </div>
//                 <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
//                   <div> 
//                     <FilterableMultiSelect
//                       label="Denied Users"
//                       options={userOptions}
//                       selected={deniedUsers}
//                       setSelected={setDeniedUsers}
//                     />
//                   </div>

//                   <div> 
//                     <FilterableMultiSelect
//                       label="Allowed Departments"
//                       options={userOptions}
//                       selected={selectedDepartments}
//                       setSelected={setSelectedDepartments}
//                     />
//                   </div>
//                 </div>
//                 <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
//                   {/* Icon Selection */}
//                   <div>
//                     <label className={`block text-xs font-medium mb-1 ${labelCls}`}>
//                       Application Icon
//                     </label>
//                     <select
//                       value={formData.icon}
//                       onChange={(e) => handleInputChange('icon', e.target.value)}
//                       className="w-full bg-neutral-800 border border-neutral-700 text-white px-3 py-2 rounded-md text-xs focus:outline-none focus:ring-2 focus:ring-indigo-500"
//                     >
//                       {iconOptions.map((option) => (
//                         <option key={option.name} value={option.name}>
//                           {option.label}
//                         </option>
//                       ))}
//                     </select>
//                   </div>

//                   {/* Tags */}
//                   <div>
//                     <label className={`block text-xs font-medium mb-1 ${labelCls}`}>
//                       Tags (comma-separated)
//                     </label>
//                     <input
//                       type="text"
//                       value={formData.tags}
//                       onChange={(e) => handleInputChange('tags', e.target.value)}
//                       className="w-full bg-neutral-800 border border-neutral-700 text-white px-3 py-2 rounded-md text-xs focus:outline-none focus:ring-2 focus:ring-indigo-500"
//                       placeholder="AI, Analytics, Enterprise, Automation"
//                     />
//                   </div>


//                 </div>
//               </div>
//             )}

//             {/* Technical Configuration Tab */}
//             {activeTab === 'technical' && (
//               <div className="space-y-4">
//                 {/* Agent Selection */}
//                 <div>
//                   <label className="block text-xs font-medium text-gray-300 mb-2">
//                     Select Agents <span className="text-gray-500">({formData.selectedAgents.length} selected)</span>
//                   </label>
//                   <div className="bg-neutral-800 border border-neutral-700 rounded-md max-h-48 overflow-y-auto">
//                     {availableAgents.map((agent) => (
//                       <div key={agent.id} className="flex items-center p-3 border-b border-neutral-700 last:border-b-0">
//                         <input
//                           type="checkbox"
//                           id={`agent-${agent.id}`}
//                           checked={formData.selectedAgents.includes(agent.id)}
//                           onChange={() => handleAgentSelection(agent.id)}
//                           className="mr-3 rounded border-neutral-600 text-indigo-600 focus:ring-indigo-500"
//                           disabled={agent.status === 'inactive'}
//                         />
//                         <div className="flex-1">
//                           <label htmlFor={`agent-${agent.id}`} className="flex items-center justify-between cursor-pointer">
//                             <div>
//                               <p className={`text-xs font-medium ${agent.status === 'active' ? 'text-white' : 'text-gray-500'}`}>
//                                 {agent.name}
//                               </p>
//                               <p className="text-xs text-gray-400">{agent.description}</p>
//                             </div>
//                             <div className="flex items-center space-x-2">
//                               <span className={`px-2 py-0.5 rounded-full text-xs ${agent.status === 'active'
//                                 ? 'bg-green-400/10 text-green-400'
//                                 : 'bg-gray-400/10 text-gray-400'
//                                 }`}>
//                                 {agent.type}
//                               </span>
//                               <span className={`w-2 h-2 rounded-full ${agent.status === 'active' ? 'bg-green-400' : 'bg-gray-400'
//                                 }`} />
//                             </div>
//                           </label>
//                         </div>
//                       </div>
//                     ))}
//                   </div>
//                 </div>
//               </div>
//             )}

//             {/* Deployment Configuration Tab */}
//             {activeTab === 'deployment' && (
//               <div className="space-y-4">
//                 {/* Frontend Configuration */}
//                 <div className="space-y-4 p-4 bg-neutral-800/50 rounded-lg border border-neutral-700">
//                   <div className="flex items-center space-x-2">
//                     <Globe size={16} className="text-indigo-400" />
//                     <h3 className="text-sm font-medium text-white">Frontend Configuration</h3>
//                   </div>

//                   <div>
//                     <label className={`block text-xs font-medium mb-1 ${labelCls}`}>
//                       Frontend URL Route
//                     </label>
//                     <input
//                       type="url"
//                       value={formData.url}
//                       onChange={(e) => handleInputChange('url', e.target.value)}
//                       className={`w-full bg-neutral-800 border text-white px-3 py-2 rounded-md text-xs focus:outline-none focus:ring-2 focus:ring-indigo-500 font-mono ${errors.url ? 'border-red-500' : 'border-neutral-700'
//                         }`}
//                       placeholder="https://your-app.yourdomain.com"
//                     />
//                     {errors.url && <p className="text-red-400 text-xs mt-1">{errors.url}</p>}
//                     <p className="text-xs text-gray-500 mt-1">The public URL where your frontend will be accessible</p>
//                   </div>

//                   {/* SPA Code */}
//                   <div>
//                     <label className={`block text-xs font-medium mb-1 ${labelCls}`}>
//                       SPA Code
//                     </label>
//                     <textarea
//                       value={formData.spacode}
//                       onChange={(e) => handleInputChange('spacode', e.target.value)}
//                       className="w-full bg-neutral-800 border border-neutral-700 text-white px-3 py-2 rounded-md text-xs focus:outline-none focus:ring-2 focus:ring-indigo-500 font-mono"
//                       rows={8}
//                       placeholder="Enter your SPA code here..."
//                     />
//                     <p className="text-xs text-gray-500 mt-1">Single Page Application code for your frontend</p>
//                   </div>

//                   <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
//                     <div>
//                       <label className={`block text-xs font-medium mb-1 ${labelCls}`}>
//                         Deploy
//                       </label>
//                       <input
//                         type="file"
//                         className="w-full bg-neutral-800 border border-neutral-700 text-white px-3 py-2 rounded-md text-xs focus:outline-none focus:ring-2 focus:ring-indigo-500 font-mono"
//                       />
//                     </div>
//                   </div>
//                 </div>
//               </div>
//             )}


//             {/* Error Display */}
//             {errors.submit && (
//               <div className="mt-4 p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
//                 <p className="text-red-400 text-xs">{errors.submit}</p>
//               </div>
//             )}
//           </div>

//           {/* Action Buttons */}
//           <div className="flex items-center justify-between p-5 border-t border-neutral-700">
//             <div className="flex items-center space-x-2">
//               <GitBranch size={14} className="text-gray-400" />
//               <span className="text-xs text-gray-400">
//                 {activeTab === 'basic' && 'Step 1 of 3: Basic Information'}
//                 {activeTab === 'technical' && 'Step 2 of 3: Technical Configuration'}
//                 {activeTab === 'deployment' && 'Step 3 of 3: Deployment Setup'}
//               </span>
//             </div>
//             <div className="flex gap-3">
//               <button
//                 type="button"
//                 onClick={onClose}
//                 className="px-4 py-2 bg-neutral-700 hover:bg-neutral-600 text-white rounded-md text-xs transition-colors"
//                 disabled={isSubmitting}
//               >
//                 Cancel
//               </button>
//               {activeTab !== 'deployment' ? (
//                 <button
//                   type="button"
//                   onClick={() => {
//                     if (activeTab === 'basic') setActiveTab('technical');
//                     else if (activeTab === 'technical') setActiveTab('deployment');
//                   }}
//                   className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-md text-xs transition-colors"
//                 >
//                   Next Step
//                 </button>
//               ) : (
//                 <button
//                   type="submit"
//                   onClick={handleSubmit}
//                   className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-md text-xs transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
//                   disabled={isSubmitting}
//                 >
//                   {isSubmitting ? 'Creating Application...' : 'Create Application'}
//                 </button>
//               )}
//             </div>
//           </div>
//         </div>
//       </div>
//     </div>
//   );
// };

// export default AddApplicationModal;

// function setRoleOptions(arg0: { label: string; value: string; }[]) {
//   throw new Error('Function not implemented.');
// }
